'use client';

import { useEffect, useState } from 'react';
import { faArrowDownToLine, faEnvelope } from '@fortawesome/pro-light-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { Sheet, SheetClose, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { Input } from '@/components/ui/input/input.component';
import { Icon } from '@/components/ui/icon.component';
import { ModalCloseButton } from '@/components/ui/modal-close-button/modal-close-button.component';
import { Button } from '@/components/ui/button/button.component';
import { useGenerateGuide } from '@/hooks/use-generate-guide.hook';
import { useToast } from '@/components/ui/use-toast';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { getLogger } from '@/logger/logger';
import { Appointment } from '@/types/appointments.types';
import { Guide } from '@/app/(main)/(pages)/(auth)/agendamentos/_components/guide/guide.component';
import { generateWhatsAppText } from '@/app/(main)/(pages)/(auth)/agendamentos/_components/card-appointment/variants/_components/appointment-options/_services/appointment-share.service';
import { generateLink } from '@/utils/formatters/generate-link';
import { shareAppointmentByEmail } from '@/services/appointment/share-appointment-by-email.service';

interface ExportGuideModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appointment: Appointment;
}

export function ExportGuideModal({ open, onOpenChange, appointment }: ExportGuideModalProps) {
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  const { generateGuide, isLoading: isGeneratingGuide } = useGenerateGuide();
  const { toast } = useToast();
  const analytics = getAnalyticsPlugin();
  const logger = getLogger('appointments.export-guide-modal');

  useEffect(() => {
    if (!open) {
      setShowEmailForm(false);
      setEmailInput('');
      setEmailError('');
    }
  }, [open]);

  const handleDownloadGuide = async () => {
    analytics.track(AnalyticsEnum.MY_APPOINTMENTS_CLICK_DOWNLOAD_GUIDE, {
      tipo_agendamento: appointment.type,
      status_agendamento: appointment.status,
    });

    await generateGuide({
      guideCode: appointment.referral,
      component: <Guide appointment={appointment} />,
    });

    onOpenChange(false);
  };

  const handleWhatsAppShare = () => {
    analytics.track(AnalyticsEnum.MY_APPOINTMENTS_CLICK_SHARE_WHATSAPP);

    try {
      const message = generateWhatsAppText(appointment);
      const whatsappUrl = generateLink({
        url: 'https://api.whatsapp.com/send',
        params: [{ key: 'text', value: message }],
      });

      const opened = window.open(whatsappUrl, '_blank');
      if (!opened) {
        toast.warn('Não foi possível abrir o WhatsApp. Verifique se popups estão bloqueados.');
        return;
      }

      onOpenChange(false);
    } catch (error) {
      logger.error('Failed to share the appointment guide', {
        appointment_guide: { event: 'share_failed', channel: 'whatsapp', appointment_id: appointment.id },
        error,
      });
      toast.error('Erro ao compartilhar guia via WhatsApp.');
    }
  };

  const handleEmailShare = async () => {
    const trimmedEmail = emailInput.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!trimmedEmail || !emailRegex.test(trimmedEmail)) {
      setEmailError('E-mail inválido.');
      return;
    }

    setIsSendingEmail(true);

    try {
      await shareAppointmentByEmail(appointment.id, trimmedEmail);

      analytics.track(AnalyticsEnum.MY_APPOINTMENTS_SEND_EMAIL_SUCCESS);
      toast.success('Guia enviada por e-mail com sucesso.');
      onOpenChange(false);
    } catch (error) {
      logger.error('Failed to share the appointment guide', {
        appointment_guide: { event: 'share_failed', channel: 'email', appointment_id: appointment.id },
        error,
      });
      toast.error('Erro ao enviar guia por e-mail.');
    } finally {
      setIsSendingEmail(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="centerFit"
        closingTagPosition="right"
        hasHeader
        className="p-0 md:bg-transparent border-none overflow-x-hidden outline-none outline-offset-0"
        data-testid={DataTestIdEnum.APPOINTMENT_CARD_EXPORT_GUIDE_MODAL}
      >
        <SheetHeader className="bg-white">
          <div className="flex justify-between gap-2 p-6 items-center">
            <SheetTitle>
              <Typography
                variant={TypographyVariant.MODAL_TITLE}
                data-testid={DataTestIdEnum.APPOINTMENT_CARD_EXPORT_GUIDE_MODAL_TITLE}
              >
                Exportar guia
              </Typography>
            </SheetTitle>
            <SheetClose asChild>
              <ModalCloseButton className="self-center" />
            </SheetClose>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto pt-0 px-6 pb-5 bg-white flex flex-col gap-5">
          <button
            type="button"
            onClick={handleDownloadGuide}
            disabled={isGeneratingGuide}
            data-testid={DataTestIdEnum.APPOINTMENT_CARD_EXPORT_GUIDE_MODAL_DOWNLOAD}
            className="flex items-center gap-2.5"
          >
            <div className="flex items-center justify-center size-8 rounded-lg bg-green-100">
              <Icon icon={faArrowDownToLine} className="text-green-500 w-4 h-4" />
            </div>
            <Typography
              variant={TypographyVariant.BODY_TEXT}
              data-testid={DataTestIdEnum.APPOINTMENT_CARD_EXPORT_GUIDE_MODAL_DOWNLOAD_LABEL}
            >
              Baixar guia
            </Typography>
          </button>

          <div className="h-px bg-gray-300 w-full" />

          <button
            type="button"
            onClick={handleWhatsAppShare}
            data-testid={DataTestIdEnum.APPOINTMENT_CARD_EXPORT_GUIDE_MODAL_WHATSAPP}
            className="flex items-center gap-2.5"
          >
            <div className="flex items-center justify-center size-8 rounded-lg bg-green-100">
              <Icon icon={faWhatsapp} className="text-green-500 w-4 h-4" />
            </div>
            <Typography
              variant={TypographyVariant.BODY_TEXT}
              data-testid={DataTestIdEnum.APPOINTMENT_CARD_EXPORT_GUIDE_MODAL_WHATSAPP_LABEL}
            >
              Compartilhar por WhatsApp
            </Typography>
          </button>

          <div className="h-px bg-gray-300 w-full" />

          {!showEmailForm
            ? (
                <button
                  type="button"
                  onClick={() => {
                    analytics.track(AnalyticsEnum.MY_APPOINTMENTS_CLICK_SEND_EMAIL);
                    setShowEmailForm(true);
                  }}
                  data-testid={DataTestIdEnum.APPOINTMENT_CARD_EXPORT_GUIDE_MODAL_EMAIL_BUTTON}
                  className="flex items-center gap-2.5"
                >
                  <div className="flex items-center justify-center size-8 rounded-lg bg-green-100">
                    <Icon icon={faEnvelope} className="text-green-500 w-4 h-4" />
                  </div>
                  <Typography
                    variant={TypographyVariant.BODY_TEXT}
                    data-testid={DataTestIdEnum.APPOINTMENT_CARD_EXPORT_GUIDE_MODAL_EMAIL_BUTTON_LABEL}
                  >
                    Enviar por e-mail
                  </Typography>
                </button>
              )
            : (
                <div className="flex flex-col gap-4">
                  <button
                    type="button"
                    onClick={() => setShowEmailForm(false)}
                    className="flex items-center gap-2.5"
                  >
                    <div className="flex items-center justify-center size-8 rounded-lg bg-green-100">
                      <Icon icon={faEnvelope} className="text-green-500 w-4 h-4" />
                    </div>
                    <Typography variant={TypographyVariant.BODY_TEXT}>
                      Enviar por e-mail
                    </Typography>
                  </button>

                  <div className="flex flex-col gap-4">
                    <Input
                      type="text"
                      placeholder="Digite o endereço de e-mail"
                      value={emailInput}
                      onChange={(e) => {
                        setEmailInput(e.target.value);
                        setEmailError('');
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          void handleEmailShare();
                        }
                      }}
                      hasError={!!emailError}
                      data-testid={DataTestIdEnum.APPOINTMENT_CARD_EXPORT_GUIDE_MODAL_EMAIL_INPUT}
                    />
                    {emailError && (
                      <Typography
                        variant={TypographyVariant.SMALL_BODY_TEXT}
                        className="text-danger-500"
                        data-testid={DataTestIdEnum.APPOINTMENT_CARD_EXPORT_GUIDE_MODAL_EMAIL_ERROR}
                      >
                        {emailError}
                      </Typography>
                    )}
                    <Button
                      type="button"
                      onClick={handleEmailShare}
                      isLoading={isSendingEmail}
                      data-testid={DataTestIdEnum.APPOINTMENT_CARD_EXPORT_GUIDE_MODAL_EMAIL_SUBMIT}
                      className="w-full"
                    >
                      Enviar
                    </Button>
                  </div>
                </div>
              )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
